import React, { useEffect, useRef, useState } from "react";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { useDispatch, useSelector } from "react-redux";
import { selectProgressPercentage } from "../../store/selectors";
import { useTranslation } from "react-i18next";
import { toggleBadgeModal, toggleShareBadgeModal } from "../../store/actions";
import useBadgeFilename from "../../hooks/useBadgeFilename";
import BadgeActiveButtons from "./BadgeActiveButtons";
import BadgeInactiveButtons from "./BadgeInactiveButtons";
import makeRequest from "../../utils/makeRequest";
import { trackAdobeAppClick, trackAdobeClick } from "../../utils/adobeLogging";

const BadgeModal = () => {
  const { t } = useTranslation();
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const dispatch = useDispatch();
  const ref = useRef(null);
  useOutsideClick(ref, () => dispatch(toggleBadgeModal()));
  const badgeFilename = useBadgeFilename();
  const badgePath = `/images/badges/${badgeFilename}`;

  const progress = useSelector(selectProgressPercentage);
  const shares = useSelector((state) => state.ui.shares);
  const section = useSelector((state) => state.progress.section);
  const lang = useSelector((state) => state.ui.lang);
  const showBadgeModal = useSelector((state) => state.ui.showBadgeModal);
  const sectionText = section === "PMAgile" ? "Agile" : "Waterfall";

  useEffect(()=>{
    if(showBadgeModal && progress===100) {
      issueCredlyBadge()
    }
  },[showBadgeModal])

  const trackDownload = () => {
    trackAdobeAppClick({ action: "download-badge", language: lang });
    trackAdobeClick(
      { fileName: badgeFilename, fileLanguage: lang },
      {
        linkTitle: t("download"),
        linkModule: `${section}-badge-modal`,
        targetURL: badgePath,
      },
      false
    );
  };
  const issueCredlyBadge =async () => {
    const data = await makeRequest("post", "/api/credly", {
      lang,
      section: section === "PMAgile" ? "agile" : "waterfall",
    });
    if (data === true) {
      setFeedbackMessage(t("badgeIssued"));
      setShowFeedback(true);
    } else {
      setFeedbackMessage(t("badgeExists"));
      setShowFeedback(true);
    }
  }
  
  const showShare = () => {
    trackAdobeAppClick({action: "share", language: lang});
    trackAdobeClick(null, {
      linkTitle: t("share"),
      linkModule: `${section}-links-sidebar`,
      targetURL: null,
    });
    dispatch(toggleBadgeModal());
    dispatch(toggleShareBadgeModal());
  };

  return (
    <div
      className="fixed z-10 inset-0 overflow-y-auto"
      adoberegion={`${section}-badge-modal`}
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity">
          <div className="absolute inset-0 bg-gray-500 opacity-75" />
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" />
        &#8203;
        <div
          className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <div>
            <div ref={ref}>
              {progress === 100 ? (
                <div>
                  <div className="text-center">
                    <h1 className="font-bold text-lg uppercase my-5">
                      {t(`badgeHeader${sectionText}`)}
                    </h1>
                    <div>{t(`badgePar1${sectionText}`)}</div>
                    <div
                      className="my-8 flex justify-center"
                      style={{
                        backgroundImage: "url(/images/badges/Confetti.jpg)",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "100% 100%",
                      }}
                    >
                      <div className="w-2/5 py-6">
                        <img src={badgePath} alt="badge" />
                      </div>
                    </div>
                    <div>{t(`badgePar2${sectionText}`, { shares })}</div>
                    <div className="grid grid-cols-2 gap-6 mt-6 mx-8">
                      <BadgeActiveButtons
                        path={badgePath}
                        showShare={showShare}
                        trackDownload={trackDownload}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-center">
                    <h1 className="font-bold text-lg uppercase my-5">
                      {t(`noBadgeHeader${sectionText}`)}
                    </h1>
                    <div className="my-5">
                      {t(`noBadgePar1${sectionText}`, { progress })}
                    </div>
                    <div className="my-5 flex justify-center">
                      <div className="w-2/5">
                        <img src={badgePath} alt="badge" />
                      </div>
                    </div>
                    <div>{t(`noBadgePar2${sectionText}`)}</div>
                    <div className="grid grid-cols-2 gap-6 mt-6 mx-8">
                      <BadgeInactiveButtons />
                    </div>
                  </div>
                </div>
              )}
              {/* TODO hidden until badging ready to deploy */}
              {/* {showFeedback && (
                <div
                  className={"w-full mt-5 rounded p-3 text-center"}
                  style={{ color: "#11667b", backgroundColor: "#e7f0f2" }}
                >
                  {feedbackMessage}
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeModal;
